import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Bell, Menu, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/constants";
import { useT, LanguageToggle } from "../lib/i18n";
import { buildNav } from "../lib/workspace";
import api from "../lib/api";
import FeedbackWidget from "./FeedbackWidget";
import { BranchBadge } from "./visual";
import { BrandLockup, BrandIcon, BrandWordmark } from "./Brand";
import GuidedReturnBanner from "./GuidedReturnBanner";

export default function Layout({ children }) {
  const { user, logout, workspaceKey } = useAuth();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [bevConfig, setBevConfig] = useState(null);

  // Beverage station nav is gated by per-branch feature flags (mother_base / espresso / counter).
  useEffect(() => {
    if (workspaceKey === "branch_bartender") {
      api.get("/beverage/my-config").then(({ data }) => setBevConfig(data)).catch(() => setBevConfig({}));
    }
  }, [workspaceKey]);

  // The sidebar is generated ONLY from the Role Template's workspace key.
  // Permissions gate ACTIONS (server-side); Branch Scope gates DATA. No hybrid logic.
  const groups = buildNav(workspaceKey, workspaceKey === "branch_bartender" ? { beverage: bevConfig || {} } : {});

  const itemActive = (n) => {
    if (n.to === "/") return location.pathname === "/";
    return location.pathname === n.to || location.pathname.startsWith(n.to + "/");
  };

  // Mobile bottom-nav = first 3 workspace items + a universal Notifications shortcut.
  const flatVisible = groups.flatMap((g) => g.items);
  const mobileItems = flatVisible.slice(0, 3).map((n) => ({ to: n.to, label: n.label, icon: n.icon }));
  mobileItems.push({ to: "/notifications", label: "Notifications", icon: Bell, badge: true });

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    if (!["SUPER_ADMIN", "OWNER", "CENTRAL_PURCHASING", "WAREHOUSE"].includes(user?.role)) return;
    api.get("/workspace/pending-orders-alert").then(({ data }) => setPendingOrders(data.pending_count || 0)).catch(() => {});
  }, [location.pathname, user?.role]);

  useEffect(() => setOpen(false), [location.pathname, location.search]);

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-2.5 px-5 py-5">
        <BrandLockup iconSize={40} subtitle={t("Restaurant Ops")} />
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
        {groups.map((g, gi) => (
          <div key={gi} className={gi > 0 ? "pt-3" : ""}>
            {g.label && (
              <p className="px-3.5 pb-1 pt-1 text-[10px] font-extrabold uppercase tracking-wider text-ink-mute" data-testid={`nav-group-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {t(g.label)}
              </p>
            )}
            {g.items.map((n) => {
              const active = itemActive(n);
              return (
                <Link
                  key={`${gi}-${n.key}`}
                  to={n.to}
                  data-testid={`nav-${n.key}`}
                  className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-all ${
                    active ? "bg-primary text-white shadow-sm" : "text-ink-soft hover:bg-stone-100"
                  }`}
                >
                  <n.icon size={18} />
                  <span className="flex-1">{t(n.label)}</span>
                  {n.to === "/notifications" && unread > 0 && (
                    <span className="rounded-full bg-danger px-1.5 py-0.5 text-[10px] font-bold text-white">{unread}</span>
                  )}
                  {n.to === "/orders" && pendingOrders > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-danger px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="nav-orders-pending-badge" title={t("Branches not yet submitted")}>
                      <span className={`h-1.5 w-1.5 rounded-full bg-white ${active ? "" : "animate-pulse"}`} />{pendingOrders}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="border-t border-line p-3">
        <div className="mb-2 flex items-center gap-3 rounded-xl px-2 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-light text-sm font-bold text-primary">
            {user?.name?.[0]}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{user?.name}</p>
            <p className="truncate text-xs text-ink-mute">{user?.station === "beverage" ? t("Bartender · Beverage Station") : t(ROLE_LABELS[user?.role])}</p>
          </div>
        </div>
        <div className="mb-2 flex items-center justify-between rounded-xl px-2">
          <span className="text-xs font-medium text-ink-mute">{t("Language")}</span>
          <LanguageToggle />
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-danger-text hover:bg-danger-light"
        >
          <LogOut size={18} /> {t("Logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-app">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-line bg-white lg:flex">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex w-full flex-col lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-line bg-white/80 px-4 py-3 backdrop-blur-md lg:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-2 lg:hidden">
            <BrandIcon size={30} />
            <BrandWordmark className="text-base" />
          </div>
          {user?.branch && (
            <div className="ml-auto mr-2 hidden sm:block">
              <BranchBadge branch={user.branch} size="sm" />
            </div>
          )}
          <LanguageToggle className={`${user?.branch ? "" : "ml-auto"} mr-1`} />
          <button onClick={() => navigate("/notifications")} className="relative rounded-lg p-2 hover:bg-stone-100" data-testid="topbar-notifications">
            <Bell size={20} className="text-ink-soft" />
            {unread > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-danger" />}
          </button>
        </header>

        <main className="flex-1 px-4 py-6 pb-24 lg:px-8 lg:pb-8"><GuidedReturnBanner />{children}</main>

        <FeedbackWidget />

        {/* Mobile bottom nav */}
        <nav className="fixed bottom-0 left-0 right-0 z-20 flex border-t border-line bg-white lg:hidden" data-testid="mobile-bottom-nav">
          {mobileItems.map((n, mi) => (
            <NavLink
              key={`${mi}-${n.to}-${n.label}`}
              to={n.to}
              end={n.to === "/"}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-semibold ${
                  isActive ? "text-primary" : "text-ink-mute"
                }`
              }
            >
              <n.icon size={20} />
              {t(n.label)}
              {n.badge && unread > 0 && (
                <span className="absolute right-[22%] top-1 h-2 w-2 rounded-full bg-danger" />
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
